import {EditorSDK, PageRef} from '@wix/platform-editor-sdk';
import {EcomPublicApi} from '../../editor-script/editorScript';
import {BiStoresReflowPageName, RuleDef} from './types';
import {Logger as WebBiLogger} from '@wix/web-bi-logger/dist/src/types';
import {guid} from '@wix/web-bi-logger/dist/src/lib/utils';
import {AppReflowRuleType, PageMap, AppReflowType} from '@wix/wixstores-client-core';
import {sendBi} from './bi';

export const APP_REFLOW_RULE_CONFIG = {
  [AppReflowRuleType.Products]: {fieldKey: 'id', priority: 10},
  [AppReflowRuleType.Categories]: {fieldKey: 'categoryId', priority: 100},
  [AppReflowRuleType.NestedCategories]: {fieldKey: 'categoryBreadcrumbs', priority: 200},
} as const;

const FIELD_KEY_TO_RULE_TYPE_MAP: Record<string, AppReflowRuleType> = Object.entries(APP_REFLOW_RULE_CONFIG).reduce<
  Record<string, AppReflowRuleType>
>((acc, [ruleType, config]) => {
  acc[config.fieldKey] = ruleType as AppReflowRuleType;
  return acc;
}, {});

export const createRule = (ruleDef?: RuleDef) => {
  const config = ruleDef?.type
    ? APP_REFLOW_RULE_CONFIG[ruleDef.type]
    : APP_REFLOW_RULE_CONFIG[AppReflowRuleType.Categories];
  const fieldKey = config.fieldKey;
  const priority = config.priority;

  return {
    name: ruleDef?.type || '',
    predicate: 'AND',
    conditions: ruleDef?.type
      ? [
          {
            field_key: fieldKey,
            operator: 'EQ',
            values: ruleDef?.values ?? [],
          },
        ]
      : [],
    priority,
  };
};

export const editRule = async (
  editorSDK: EditorSDK,
  ecomPublicApi: EcomPublicApi,
  t: (key: string) => string,
  variantPageRef: PageRef,
  webBiLogger: WebBiLogger,
  metaSiteId: string,
  biStoresReflowPageName: BiStoresReflowPageName
) => {
  const biFlowId = guid();

  const pageData = await editorSDK.pages.data.get('', {
    pageRef: variantPageRef,
  });
  const pageVariantRule = await (editorSDK.editor.routers as any).getVariantPageRule({variantPageRef});
  let ruleDefToEdit: RuleDef;
  if (pageVariantRule.conditions.length) {
    const fieldKey = pageVariantRule.conditions[0].field_key;
    ruleDefToEdit = {
      type: FIELD_KEY_TO_RULE_TYPE_MAP[fieldKey] || AppReflowRuleType.Categories,
      values: pageVariantRule.conditions[0].values,
    };
  }
  const updatedRuleDef: RuleDef = await openRulesModal(
    editorSDK,
    ecomPublicApi,
    t,
    pageData.title,
    metaSiteId,
    pageData.tpaPageId === PageMap.CATEGORY_PAGE_VARIANT ? AppReflowType.CategoryPage : AppReflowType.ProductPage,
    ruleDefToEdit
  );
  if (updatedRuleDef?.type) {
    const updatedRule = createRule(updatedRuleDef);
    await (editorSDK.editor.routers as any).updateVariantPageRule({variantPageRef, updatedRule, biFlowId});
  }
  return sendBi({
    biStoresReflowPageName,
    webBiLogger,
    isEdit: true,
    pageVariantId: variantPageRef.id,
    originalPageId: '',
    duplicatedPageId: '',
    pageVariantType: '',
    ruleDef: updatedRuleDef,
    flowId: biFlowId,
    msid: metaSiteId,
  });
};

export const openRulesModal = (
  editorSDK: EditorSDK,
  ecomPublicApi: EcomPublicApi,
  t: (key: string) => string,
  pageTitle: string,
  metaSiteId: string,
  appReflowType: AppReflowType,
  rule?: RuleDef
): Promise<RuleDef> => {
  const pickerModalUrl = `https://editor.wix.com/wixstores-client-editor-modals/?metaSiteId=${metaSiteId}`;

  return editorSDK.editor.openModalPanel('', {
    url: pickerModalUrl,
    title: `${t(
      appReflowType === AppReflowType.ProductPage
        ? 'newProductPage.assignModal.header.title'
        : 'newCategoryPage.assignModal.header.title'
    )} ${pageTitle}`,
    initialData: {instance: ecomPublicApi.getInstance(), rule, appReflowType},
    height: 452,
    width: 700,
  });
};
